import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import useApi from 'hooks/useApi';
import { getSummary } from 'api/orders';
import OrderList from './components/OrderList';
import { toast } from 'react-hot-toast';

import printIcon from 'assets/print.svg';
import DatePicker from 'components/DatePicker';
import CTA from 'components/CTA';

import MobileDateHeader from './components/MobileDateHeader';
import AnalyticsTab from './components/AnalyticsTab/AnalyticsTab';
import PrintComponent from 'components/PrintComponent';
import Loading from 'components/Loading';

import { getCurrentWeekFromDate } from 'utils/dates';
import details from './details';
import styles from './home.module.scss';

import { useReactToPrint } from 'react-to-print';
import { useDispatch, useSelector } from 'react-redux';

import { setOrdersRd } from '../../redux/slices/thunks';
import { clearList, setOrderList } from '../../redux/slices/orderSlice';
import Banner from '../../components/Banner/Banner';
import { HomeLayouts } from '../../components/layouts/HomeLayouts';
import { clearParamsO } from '../../redux/slices/paramsSlice';
import BannerFree from '../../components/Banner/BannerFree';
import moment from 'moment';
import { gatTotalOrdersBymonth } from '../../api/chart';

const MONTHS_ES = [
  'enero','febrero','marzo','abril','mayo','junio',
  'julio','agosto','septiembre','octubre','noviembre','diciembre',
];

const Home = ({ restaurantr }) => {
  const navigate = useNavigate();
  const { user, restaurant, subscription, token } = useSelector((state) => state.auth);
  const { orders_allowed, dataO, isLoadingO, errorO } = useSelector((state) => state.orderReducer);
  const componentRef = useRef();
  const dispatch = useDispatch();
  const handlePrint = useReactToPrint({ content: () => componentRef.current });

  const refObj = {
    0: useRef(null), 1: useRef(null), 2: useRef(null),
    3: useRef(null), 4: useRef(null), 5: useRef(null), 6: useRef(null),
  };

  const { data, error, isLoading } = useApi(null, true);
  const [activeTab, setActiveTab] = useState('calendario');
  const [date, setDate] = useState(new Date());

  const { monday, tuesday, wednesday, thursday, friday, saturday, sunday } =
    getCurrentWeekFromDate(date);

  const [ordersByWeek, setOrdersByWeek] = useState([]);

  const makeWeekDays = () => [
    { date: monday, orders: [] },
    { date: tuesday, orders: [] },
    { date: wednesday, orders: [] },
    { date: thursday, orders: [] },
    { date: friday, orders: [] },
    { date: saturday, orders: [] },
    { date: sunday, orders: [] },
  ];

  useEffect(() => {
    dispatch(clearParamsO());
    dispatch(setOrderList(makeWeekDays()));
    setOrdersByWeek(makeWeekDays());
    if (restaurant) {
      dispatch(setOrdersRd(getSummary, {
        from: monday, to: sunday,
        offset: new Date().getTimezoneOffset(),
        id_user: user.id,
        id_restaurant: parseInt(restaurant) || restaurantr,
      }, token));
    }
  }, [date, restaurant]);

  if (error) toast.error(errorO);

  const now = new Date();
  const dateLabel = `${now.getDate()} de ${MONTHS_ES[now.getMonth()]} ${now.getFullYear()}`;

  const tabStyle = (tab) => ({
    padding: '9px 22px',
    background: 'none',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid #2D6A4F' : '2px solid transparent',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: activeTab === tab ? 700 : 400,
    color: activeTab === tab ? '#1a1a1a' : '#999',
    transition: 'all .2s',
    marginBottom: -1,
    outline: 'none',
    whiteSpace: 'nowrap',
    letterSpacing: 0.1,
  });

  return (
    <HomeLayouts>
      <div>
        {subscription?.status === 'cancelada' && <Banner />}
        {orders_allowed?.status === true && orders_allowed.number <= 0 && <BannerFree />}

        <div className={styles.content}>

          {/* ── Dashboard Header ──────────────────────────────── */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: 16,
          }}>
            {/* Lado izquierdo: saludo + título */}
            <div>
              <p style={{
                fontSize: 12, fontWeight: 500, color: '#49A35B',
                letterSpacing: 1.8, textTransform: 'uppercase',
                margin: '0 0 6px',
              }}>
                Hola, {user?.name?.split(' ')[0]} · {dateLabel}
              </p>
              <h1 style={{
                fontSize: 32, fontWeight: 800, color: '#111',
                margin: 0, letterSpacing: -1, lineHeight: 1.05,
              }}>
                Resumen
              </h1>
            </div>

            {/* Lado derecho: CTA */}
            {subscription?.status !== 'cancelada' && user?.roles[0]?.name !== 'Staff' && (
              <CTA
                isDisabled={orders_allowed?.status === true && orders_allowed.number <= 0}
                view="tablet"
                text="Nueva Orden"
                onClick={() => {
                  localStorage.setItem('step', 1);
                  localStorage.setItem('state', JSON.stringify({}));
                  navigate('/ordenes/nueva');
                }}
                size="small"
              />
            )}
          </div>

          {/* ── Tab Navigation ────────────────────────────────── */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid #eeebe5',
            marginTop: -8,
            gap: 0,
          }}>
            <button style={tabStyle('calendario')} onClick={() => setActiveTab('calendario')}>
              Calendario
            </button>
            <button style={tabStyle('analitica')} onClick={() => setActiveTab('analitica')}>
              Analítica
            </button>
          </div>

          {/* ── Tab: Calendario ───────────────────────────────── */}
          {activeTab === 'calendario' && (
            <>
              <MobileDateHeader
                monday={monday} tuesday={tuesday} wednesday={wednesday}
                thursday={thursday} friday={friday} saturday={saturday} sunday={sunday}
                date={date} setDate={setDate} refObj={refObj}
              />

              <div className={styles.detailsContainer}>
                <div className={styles.flexHeader}>
                  <h2 className={styles.subHeader}>Detalles</h2>
                  <DatePicker
                    restaurant={restaurant}
                    startDate={monday} endDate={sunday}
                    date={date} setDate={setDate}
                  />
                </div>
                <div className={styles.detailsGrid}>
                  {details.map((detail, i) => {
                    if (user?.approval === false && detail.name === 'En revisión') return null;
                    return (
                      <div key={`${i}_${detail.name}`} className={styles.detail} style={{ backgroundColor: detail.bgHex }}>
                        <img src={detail.icon} alt="icon" className={styles.icon} />
                        <div>
                          <h2 className={styles.subHeader}>{dataO ? dataO.statistics[detail.key] : '0'}</h2>
                          <h3 style={{ color: detail.titleHex }}>{detail.name}</h3>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={styles.scroll}>
                <div className={styles.flexHeader}>
                  <h2 className={styles.subHeader}>Órdenes</h2>
                  <button className={styles.printButton} onClick={handlePrint}>
                    <img src={printIcon} alt="printIcon" />
                    <span>Imprimir</span>
                  </button>
                </div>
                {ordersByWeek.length === 0 ? (
                  <Loading />
                ) : (
                  <OrderList
                    ordersByWeek={ordersByWeek}
                    data={dataO}
                    setOrdersByWeek={setOrdersByWeek}
                    isloa={isLoading}
                    isLoading={isLoadingO}
                    refObj={refObj}
                  />
                )}
              </div>
            </>
          )}

          {/* ── Tab: Analítica ────────────────────────────────── */}
          {activeTab === 'analitica' && <AnalyticsTab />}

        </div>

        <PrintComponent orders={ordersByWeek} ref={componentRef} />
      </div>
    </HomeLayouts>
  );
};

export default Home;
